import type { HttpCore } from '../http';
import type { CountryCode } from './me';

/** Mirrors the payments section of `frontend/web-ide/src/services/portal.ts`. */

export type CheckoutStatus = 'pending' | 'succeeded' | 'expired' | 'failed' | 'refunded';

export interface CreateCheckoutSessionRequest {
  cert_slug: string;
  country_code: CountryCode;
  quiz_attempt_id?: string;
}

export interface CreateCheckoutSessionResponse {
  url: string;
  session_id: string;
  vertical_slug: string;
  amount_cents: number;
  currency: string;
}

export interface CheckoutStatusResponse {
  status: CheckoutStatus;
  vertical_slug: string | null;
  cert_slug: string;
}

export interface CreateJazzCashTransactionRequest {
  cert_slug: string;
  quiz_attempt_id?: string;
}

export interface CreateJazzCashTransactionResponse {
  action_url: string;
  fields: Record<string, string>;
  txn_ref_no: string;
  vertical_slug: string;
  amount_cents: number;
  currency: string;
}

export function createPaymentsModule(core: HttpCore) {
  return {
    /**
     * Mints a Stripe Checkout Session for the cert's vertical. Requires an
     * authenticated learner. Returns the Stripe-hosted `url` to redirect to.
     */
    createCheckoutSession: (input: CreateCheckoutSessionRequest) =>
      core.request<CreateCheckoutSessionResponse>('/payments/checkout/create-session', {
        method: 'POST',
        body: input,
      }),
    /** Poll target for the post-Stripe return page; reads our own payment_intents record. */
    getCheckoutStatus: (sessionId: string) =>
      core.request<CheckoutStatusResponse>(`/payments/checkout/session/${encodeURIComponent(sessionId)}`),
    /**
     * PK direct wallet flow. Unlike Stripe, returns hash-signed form `fields`
     * the caller must auto-submit as a real HTML form POST (not fetch/XHR)
     * to `action_url` — JazzCash has no hosted-checkout equivalent.
     */
    createJazzCashTransaction: (input: CreateJazzCashTransactionRequest) =>
      core.request<CreateJazzCashTransactionResponse>('/payments/jazzcash/checkout/create-session', {
        method: 'POST',
        body: input,
      }),
    getJazzCashStatus: (txnRefNo: string) =>
      core.request<CheckoutStatusResponse>(`/payments/jazzcash/checkout/status/${encodeURIComponent(txnRefNo)}`),
  };
}

export type PaymentsModule = ReturnType<typeof createPaymentsModule>;
